import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Check, Trash2, Mail, Users, Calendar, Trophy, Building2, X } from 'lucide-react';
import { toast } from 'sonner';
import { haceCuanto } from '../lib/utils';

import { API_URL } from '../lib/apiUrl';
import * as Popover from '@radix-ui/react-popover';

const NOTIFICATION_ICONS = {
  team_invite: Users,
  org_invite: Building2,
  assigned_activity: Calendar,
  session_updated: Calendar,
  mention: Mail,
  comment: Mail,
};

const NOTIFICATION_COLORS = {
  team_invite: 'var(--primary)',
  org_invite: '#a78bfa',
  assigned_activity: '#4dd6ff',
  session_updated: '#ffd34d',
  mention: '#ff4d6d',
  comment: '#4dd6ff',
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (showPanel) {
      fetchNotifications();
    }
  }, [showPanel]);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/unread-count`);
      setUnreadCount(res.data.count || 0);
    } catch (e) {
      console.error('Error fetching unread count:', e);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/notifications`);
      setNotifications(res.data || []);
    } catch (e) {
      console.error('Error fetching notifications:', e);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationIds) => {
    try {
      await axios.post(`${API_URL}/api/notifications/mark-read`, notificationIds);
      fetchUnreadCount();
      fetchNotifications();
    } catch (e) {
      toast.error('Error al marcar como leída');
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read_at) {
      markAsRead([notification.id]);
    }
    
    // Navigate to action URL
    if (notification.action_url) {
      setShowPanel(false);
      navigate(notification.action_url);
    }
  };

  const markAllAsRead = () => {
    const unreadIds = notifications.filter(n => !n.read_at).map(n => n.id);
    if (unreadIds.length > 0) {
      markAsRead(unreadIds);
    }
  };

  return (
    <Popover.Root open={showPanel} onOpenChange={setShowPanel}>
    <div className="relative" data-testid="notification-bell">
      {/* Bell Button */}
      <Popover.Trigger asChild>
      <button
        aria-label="Notificaciones"
        title="Notificaciones"
        className="relative p-2 rounded-xl transition-colors duration-200"
        style={{ background: showPanel ? 'var(--primary-soft)' : 'rgba(255,255,255,0.04)', color: showPanel ? 'var(--primary)' : 'var(--text-2)' }}
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black"
            style={{ background: '#ff4d6d', color: '#fff', fontFamily: 'Inter, sans-serif' }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      </Popover.Trigger>

      {/* Dropdown Panel */}
      {showPanel && (
        <Popover.Portal>
          {/* Panel */}
          <Popover.Content
            side="top" align="start" sideOffset={8} collisionPadding={12}
            aria-label="Notificaciones"
            className="flex flex-col overflow-hidden rounded-2xl z-[120]"
            style={{ width: 'min(384px, calc(100vw - 24px))', maxHeight: 'min(32rem, var(--radix-popover-content-available-height))', background: 'var(--surface-1)', border: '1px solid var(--border-strong)', boxShadow: '0 16px 48px rgba(15,23,42,0.20)' }}
            data-testid="notifications-panel"
          >
            {/* Header */}
            <div className="flex flex-shrink-0 items-center justify-between gap-2 px-4 py-4" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              <h3 className="text-sm font-black" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--text-0)' }}>
                NOTIFICACIONES
              </h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs px-2 py-1 rounded-lg font-semibold"
                    style={{ background: 'var(--primary-soft)', color: 'var(--primary)' }}
                  >
                    <Check className="w-3 h-3 inline mr-1" />
                    Marcar todas
                  </button>
                )}
                <button
                  aria-label="Cerrar notificaciones"
                  onClick={() => setShowPanel(false)}
                  className="p-1 rounded-lg"
                  style={{ color: 'var(--text-2)' }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto min-h-0 overscroll-contain">
              {loading ? (
                <div className="p-4 space-y-2">
                  {Array(3).fill(0).map((_, i) => <div key={i} className="skeleton h-16 rounded-xl" />)}
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--text-2)', opacity: 0.4 }} />
                  <p className="text-sm" style={{ color: 'var(--text-2)' }}>No tienes notificaciones</p>
                </div>
              ) : (
                <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
                  {notifications.map(notification => {
                    const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
                    const color = NOTIFICATION_COLORS[notification.type] || 'var(--primary)';
                    const isUnread = !notification.read_at;
                    
                    return (
                      <button
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className="w-full px-5 py-4 text-left transition-colors duration-200 hover:bg-white/[0.02]"
                        style={{ background: isUnread ? 'rgba(var(--primary-rgb),0.03)' : 'transparent' }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
                            <Icon className="w-5 h-5" style={{ color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm mb-1" style={{ color: isUnread ? 'var(--text-0)' : 'var(--text-1)', fontWeight: isUnread ? 600 : 400 }}>
                              {notification.message}
                            </p>
                            <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                              {haceCuanto(notification.created_at)}
                            </p>
                          </div>
                          {isUnread && (
                            <div className="w-2 h-2 rounded-full flex-shrink-0 mt-2" style={{ background: color }} />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      )}
    </div>
    </Popover.Root>
  );
}
